import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { MapPin, RefreshCw, AlertCircle, Loader2, Truck, Navigation, Wifi, WifiOff, Maximize, Minimize, Play, Pause, Zap, Clock } from "lucide-react";
import { toast } from "react-toastify";
import { useApi } from "../../../hooks/useApi";

export default function MapplsMap() {
  const [searchParams] = useSearchParams();
  const tripId = searchParams.get('tripId');
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [highlightedPoint, setHighlightedPoint] = useState(null);
  const [allTripPoints, setAllTripPoints] = useState([]);
  const [visiblePoints, setVisiblePoints] = useState(new Set()); // Track which points are visible on map
  const [processingPoint, setProcessingPoint] = useState(null); // Track which point is being processed
  const [lastClickTime, setLastClickTime] = useState(0); // Debounce clicks
  const [mapHighlighted, setMapHighlighted] = useState(false); // Track if map is highlighted
  const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
  const [selectedPointDetails, setSelectedPointDetails] = useState(null); // Track selected point for mobile card
  const [isMobile, setIsMobile] = useState(false); // Track if device is mobile
  const [cardState, setCardState] = useState('normal'); // Track card animation state: 'normal', 'expanded', 'collapsed'
  const [isDragging, setIsDragging] = useState(false); // Track if card is being dragged
  const [dragStartY, setDragStartY] = useState(0); // Track drag start position
  const [dragCurrentY, setDragCurrentY] = useState(0); // Track current drag position
  const [cardHeight, setCardHeight] = useState(40); // Track card height percentage
  const [showMobileDetails, setShowMobileDetails] = useState(false); // Track mobile details view
  const [isAnimating, setIsAnimating] = useState(false); // Track if animation is running
  const [showSpeedControls, setShowSpeedControls] = useState(false); // Track if speed controls are visible
  const [animationSpeed, setAnimationSpeed] = useState('fast'); // 'fast' or 'slow'
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0); // Current waypoint in animation
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const intervalRef = useRef(null);
  const animationMarkerRef = useRef(null); // Reference to animated marker
  const animationIntervalRef = useRef(null); // Reference to animation interval
  const animationFrameIdRef = useRef(null); // Reference to animation frame ID
  const isAnimatingRef = useRef(false); // Ref to track animation state for closures
  const { get } = useApi();

  // Function to format date as DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Function to format date and time as DD/MM/YYYY HH:MM:SS
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Function to get marker color based on position
  const getMarkerColor = (i, routeLength) => 
    i === 0 ? "#22c55e" : (i === routeLength - 1 ? "#ef4444" : "#22c55e");

  // Function to highlight a specific point on the map
  const highlightPoint = (pointId, tripId) => {
    try {
      // Debounce rapid clicks
      const now = Date.now();
      if (now - lastClickTime < 500) {
        return;
      }
      setLastClickTime(now);
      
      if (!mapInstance.current) {
        toast.error('Map not ready. Please wait for map to load.');
        return;
      }
      
      // Find the point in all trip points
      const point = allTripPoints.find(p => p.id === pointId && p.tripId === tripId);
      if (!point) {
        toast.error('WayPoint not found in data');
        return;
      }
      
      // Set processing state
      setProcessingPoint(pointId);
      setHighlightedPoint(pointId);
      
      // Add this point to visible points
      const pointKey = `${tripId}-${pointId}`;
      setVisiblePoints(prev => new Set([...prev, pointKey]));
      
      // 1️⃣ FIRST: Show this specific point on the map immediately
      showSpecificPointOnMap(point, tripId, selectedTrip);
      
      // 2️⃣ SECOND: Navigate map to the point with better zoom and bounds
      try {
        // First, set center and zoom
        mapInstance.current.setCenter([point.longitude, point.latitude]);
        mapInstance.current.setZoom(16);
        
        // Also try to fit bounds to ensure the point is visible
        const bounds = new window.mappls.LatLngBounds();
        bounds.extend({
          lat: parseFloat(point.latitude),
          lng: parseFloat(point.longitude)
        });
        
        // Add some padding around the point
        const latPadding = 0.01; // ~1km
        const lngPadding = 0.01; // ~1km
        
        const extendedBounds = [
          [point.longitude - lngPadding, point.latitude - latPadding],
          [point.longitude + lngPadding, point.latitude + latPadding]
        ];
        
        mapInstance.current.fitBounds(extendedBounds, {
          padding: 50,
          maxZoom: 12,
          duration: 1000
        });
        
      } catch (error) {
        // Fallback: just set center and zoom
        try {
          mapInstance.current.setCenter([point.longitude, point.latitude]);
          mapInstance.current.setZoom(16);
        } catch (fallbackError) {
        }
      }
      
      // 3️⃣ THIRD: Create detailed point object for mobile card
      const detailedPoint = {
        id: point.id,
        tripId: point.tripId || tripId,
        trip_id: point.trip_id,
        tripName: point.tripName,
        latitude: point.latitude,
        longitude: point.longitude,
        speed: point.speed,
        pointTime: point.pointTime,
        point_time: point.point_time,
        sequence: point.sequence,
        totalPoints: point.totalPoints,
        isStart: point.isStart,
        isEnd: point.isEnd,
        color: point.color,
        duration: point.duration,
        distance: point.distance,
        // Add additional trip information if available
        tripStartTime: selectedTrip?.trip?.trip_start_time,
        tripEndTime: selectedTrip?.trip?.trip_end_time,
        tripStatus: selectedTrip?.trip?.trip_status,
        terminalId: selectedTrip?.trip?.terminal_id || point.tripId,
        terminal_id: point.terminal_id || selectedTrip?.trip?.terminal_id,
        start_lat: selectedTrip?.trip?.start_lat,
        start_lng: selectedTrip?.trip?.start_lng,
        end_lat: selectedTrip?.trip?.end_lat,
        end_lng: selectedTrip?.trip?.end_lng,
        created_at: selectedTrip?.trip?.created_at
      };
      
      
      // 4️⃣ FOURTH: Set selected point details for mobile card (this will open the card)
      // Add a small delay to ensure marker is rendered before opening card
      setTimeout(() => {
        setSelectedPointDetails(detailedPoint);
        
        // 5️⃣ FIFTH: Close points panel on mobile when a point is selected
        if (isMobile) {
          setShowFilter(false);
        }
        
        // 6️⃣ SIXTH: Ensure mobile card is visible and properly positioned
        if (isMobile) {
          // Reset card state to normal to ensure it's visible
          setCardState('normal');
          setCardHeight(40);
          setShowMobileDetails(false); // Make sure we're showing the point details, not the placeholder
        }
      }, 500); // Increased delay to ensure marker appears and map navigation completes
      
      setProcessingPoint(null);
      
      // Highlight the entire map with blue overlay for 10 seconds
      setMapHighlighted(true);
      setTimeout(() => {
        setMapHighlighted(false);
      }, 10000);
      
      // Show success toast
      toast.success(`📍 WayPoint ${point.sequence} displayed on map!`, {
        position: isMobile ? "top-center" : "top-right",
        autoClose: 2000,
        className: isMobile ? "toast-mobile" : "",
        bodyClassName: isMobile ? "toast-mobile-body" : "",
        progressClassName: isMobile ? "toast-mobile-progress" : "",
      });
    } catch (error) {
      setProcessingPoint(null);
      toast.error('Failed to select WayPoint. Please try again.', {
        position: isMobile ? "top-center" : "top-right",
        className: isMobile ? "toast-mobile" : "",
        bodyClassName: isMobile ? "toast-mobile-body" : "",
        progressClassName: isMobile ? "toast-mobile-progress" : "",
      });
    }
  };

  // Function to show a specific point on the map
  
  /*const showSpecificPointOnMap = (point, tripId) => {
    try {
      if (!mapInstance.current || !point?.latitude || !point?.longitude) return;

      const lat = parseFloat(point.latitude);
      const lng = parseFloat(point.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      // 1️⃣ Determine icon color and label
      let color = "#22c55e"; // green
      let label = "S";
      if (point.isEnd) {
        color = "#ef4444";
        label = "E";
      } else if (!point.isStart) {
        color = "#3b82f6";
        label = "P";
      }

      // 2️⃣ Create base Mappls marker
      const marker = new window.mappls.Marker({
        map: mapInstance.current,
        position: { lat, lng },
        popupHtml: `
          <div style="padding: 8px; min-width: 260px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600; color: #111827;">
              ${point.isStart ? "🚀 Trip Start" : point.isEnd ? "🏁 Trip End" : `📍 WayPoint ${point.sequence || "N/A"}`}
            </h3>
            <div style="font-size: 12px; color: #6b7280;">
              <p><b>Trip ID:</b> ${tripId}</p>
              <p><b>Speed:</b> ${point.speed || 0} km/h</p>
              <p><b>Coords:</b> ${lat.toFixed(6)}, ${lng.toFixed(6)}</p>
            </div>
          </div>
        `,
        fitbounds: false
      });

      // Store in marker reference array
      marker._tripId = tripId;
      marker._pointId = point.id;
      markersRef.current.push(marker);

      // 3️⃣ Helper: create & apply custom SVG icon
      const applyCustomSVG = () => {
        const el = marker.getElement?.();
        if (!el) return;

        const svgIcon = `
          <svg width="44" height="44" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="28" fill="${color}" stroke="white" stroke-width="4"/>
            <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">${label}</text>
          </svg>`;
        const iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgIcon)}`;

        // Apply SVG icon styling
        el.style.backgroundImage = `url("${iconUrl}")`;
        el.style.backgroundRepeat = "no-repeat";
        el.style.backgroundSize = "44px 44px";
        el.style.width = "44px";
        el.style.height = "44px";
        el.style.borderRadius = "50%";
        el.style.cursor = "pointer";
        el.style.transition = "transform 0.25s ease";
        el.style.transform = "translate(-22px, -22px)"; // anchor center-center

        // 4️⃣ Add glow animation styles once
        if (!document.getElementById("mapplsPulseStyle")) {
          const styleTag = document.createElement("style");
          styleTag.id = "mapplsPulseStyle";
          styleTag.innerHTML = `
            @keyframes pulseGreen {
              0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); transform: translate(-22px,-22px) scale(1); }
              50% { box-shadow: 0 0 10px 6px rgba(34,197,94,0.6); transform: translate(-22px,-22px) scale(1.1); }
              100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); transform: translate(-22px,-22px) scale(1); }
            }
            @keyframes pulseRed {
              0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); transform: translate(-22px,-22px) scale(1); }
              50% { box-shadow: 0 0 10px 6px rgba(239,68,68,0.6); transform: translate(-22px,-22px) scale(1.1); }
              100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); transform: translate(-22px,-22px) scale(1); }
            }
          `;
          document.head.appendChild(styleTag);
        }

        // 5️⃣ Apply animations
        if (point.isStart) el.style.animation = "pulseGreen 2s infinite";
        if (point.isEnd) el.style.animation = "pulseRed 2s infinite";

        // 6️⃣ Force correct map positioning again
        marker.setPosition({ lat, lng });
      };

      // Apply SVG after Mappls DOM render
      setTimeout(applyCustomSVG, 400);

      // 7️⃣ Reapply icon + recenter smoothly on click
      marker.addListener("click", () => {

        setTimeout(() => {
          applyCustomSVG();
          // Smoothly pan the map to the clicked marker
          const map = mapInstance.current;
          if (map && map.panTo) {
            map.panTo({ lat, lng }, { duration: 800 });
          }
        }, 200);
      });

    } catch (error) {
    }
  };*/

  const showSpecificPointOnMap = (point, tripId, tripData = null) => {
  try {
    if (!mapInstance.current || !point?.latitude || !point?.longitude) return;

    const lat = parseFloat(point.latitude);
    const lng = parseFloat(point.longitude);
    if (isNaN(lat) || isNaN(lng)) return;

    // Use tripData if provided, otherwise fall back to selectedTrip
    const trip = tripData?.trip || selectedTrip?.trip || null;

    // Determine icon and popup
    let iconUrl = null;
    let popupTitle = "📍 WayPoint";
    let popupColor = "#1e3a8a";

    if (point.isStart) {
      popupTitle = "🚀 Trip Start";
      popupColor = "#22c55e";
      iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="48" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="28" fill="#22c55e" stroke="white" stroke-width="4"/>
          <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">S</text>
        </svg>
      `)}`;
    } else if (point.isEnd) {
      popupTitle = "🏁 Trip End";
      popupColor = "#ef4444";
      iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="28" fill="#ef4444" stroke="white" stroke-width="4"/>
          <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">E</text>
        </svg>
      `)}`;
    } else {
      // Midpoint (blue pulse)
      iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="24" fill="#3b82f6" stroke="white" stroke-width="4"/>
        </svg>
      `)}`;
    }

    // Create marker
    const marker = new window.mappls.Marker({
      map: mapInstance.current,
      position: { lat, lng },
      icon: iconUrl,
      width: 42,
      height: 42,
      fitbounds: false,
      popupHtml: `
        <div style="padding:6px;min-width:230px;max-width:230px;max-height:400px;overflow-y:auto;">
          <h3 style="margin:0 0 10px;font-size:13px;font-weight:600;color:${popupColor};border-bottom:2px solid ${popupColor};padding-bottom:6px;">${popupTitle}</h3>
          
          <!-- Trip Information -->
          <div style="margin-bottom:10px;padding:6px;">
            <p style="margin:3px 0;font-size:10px;color:#374151;font-weight:600;">TRIP INFORMATION</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Trip ID:</b> ${trip?.id || point.trip_id || tripId || 'N/A'}</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Status:</b> ${trip?.trip_status || 'N/A'}</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Start:</b> ${trip?.trip_start_time ? formatDateTime(trip.trip_start_time) : 'N/A'}</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>End:</b> ${trip?.trip_end_time ? formatDateTime(trip.trip_end_time) : 'N/A'}</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Duration:</b> ${trip?.duration_minutes || 'N/A'} min</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Distance:</b> ${trip?.distance_km ? trip.distance_km.toFixed(2) : 'N/A'} km</p>
            ${trip?.created_at ? `<p style="margin:1px 0;font-size:10px;color:#374151;"><b>Created:</b> ${formatDate(trip.created_at)}</p>` : ''}
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Terminal ID:</b> ${point.terminal_id || trip?.device_id || tripId || 'N/A'}</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Speed:</b> ${point.speed || 0} km/h</p>
            <p style="margin:1px 0;font-size:10px;color:#374151;"><b>Point Time:</b> ${point.point_time ? formatDateTime(point.point_time) : 'N/A'}</p>
            <p style="margin:1px 0;font-size:10px;color:#6b7280;"><b>Coords:</b> ${lat.toFixed(6)}, ${lng.toFixed(6)}</p>
          </div>
        </div>
      `,
    });

    marker._tripId = tripId;
    marker._pointId = point.id;
    marker._isSpecificPoint = true; // Mark as specific point for highlighting
    marker._fullPointData = point; // Store full point data for click handling
    marker._tripData = tripData; // Store trip data for click handling
    marker._trip = trip; // Store trip object for click handling
    markersRef.current.push(marker);

    // Add click event
    marker.addListener("click", () => {
      const map = mapInstance.current;
      if (map?.panTo) map.panTo({ lat, lng }, { duration: 800 });
      
      // Get trip data from marker or fallback to selectedTrip
      const markerTrip = marker._trip || (marker._tripData?.trip) || selectedTrip?.trip || null;
      const markerTripData = marker._tripData || selectedTrip;
      
      // Open mobile card with point details
      if (isMobile) {
        const detailedPoint = {
          id: point.id,
          tripId: point.trip_id || tripId,
          trip_id: point.trip_id || tripId,
          tripName: markerTrip?.name || `Trip ${tripId}`,
          latitude: point.latitude,
          longitude: point.longitude,
          speed: point.speed,
          pointTime: point.point_time,
          point_time: point.point_time,
          sequence: point.sequence || 0,
          totalPoints: markerTripData?.points?.length || selectedTrip?.points?.length || 0,
          isStart: point.isStart,
          isEnd: point.isEnd,
          color: point.color,
          duration: markerTrip?.duration_minutes,
          distance: markerTrip?.distance_km,
          tripStartTime: markerTrip?.trip_start_time,
          tripEndTime: markerTrip?.trip_end_time,
          tripStatus: markerTrip?.trip_status,
          terminalId: markerTrip?.device_id || point.terminal_id || tripId,
          terminal_id: point.terminal_id || markerTrip?.device_id,
          start_lat: markerTrip?.start_lat,
          start_lng: markerTrip?.start_lng,
          end_lat: markerTrip?.end_lat,
          end_lng: markerTrip?.end_lng,
          created_at: markerTrip?.created_at
        };
        setSelectedPointDetails(detailedPoint);
        setShowFilter(false);
        setCardState('normal');
        setCardHeight(40);
        setShowMobileDetails(false);
      }
    });

  } catch (error) {
  }
};

  
  const showTripPoints = (tripData, tripId, fullTripData = null) => {
    if (!tripData || !Array.isArray(tripData.points) || tripData.points.length === 0) return;

    // ✅ Try to find start & end markers from backend fields
    let startPoint =
      tripData.points.find(p => p.isStart || p.type === "start" || p.point_type === "start");
    let endPoint =
      tripData.points.find(p => p.isEnd || p.type === "end" || p.point_type === "end");

    // ✅ If backend did not provide explicit start/end — use first/last points
    if (!startPoint) startPoint = tripData.points[0];
    if (!endPoint) endPoint = tripData.points[tripData.points.length - 1];

    // ✅ Render start & end markers - pass full trip data
    if (startPoint) showSpecificPointOnMap({ ...startPoint, isStart: true }, tripId, fullTripData);
    if (endPoint) showSpecificPointOnMap({ ...endPoint, isEnd: true }, tripId, fullTripData);

    // 🟦 Optional: show midpoints later or on toggle
    // tripData.points
    //   .slice(1, -1)
    //   .forEach(p => showSpecificPointOnMap(p, tripId));
  };

  // Function to close mobile card and show points panel
  const closeMobileCard = () => {
    closeMobileCardOnly();
  };

  // Function to toggle mobile details view
  const toggleMobileDetails = () => {
    setShowMobileDetails(!showMobileDetails);
    if (!showMobileDetails) {
      // When opening details, reset card state
      setCardState('normal');
      setCardHeight(40);
    }
  };

  // Function to expand mobile card
  const expandMobileCard = () => {
    if (cardState === 'expanded') {
      // If already expanded, return to normal
      setCardState('normal');
      setCardHeight(40);
    } else {
      // Expand the card
      setCardState('expanded');
      setCardHeight(75);
    }
  };

  // Function to collapse mobile card
  const collapseMobileCard = () => {
    if (cardState === 'collapsed') {
      // If already collapsed, return to normal
      setCardState('normal');
      setCardHeight(40);
    } else {
      // Collapse the card
      setCardState('collapsed');
      setCardHeight(20);
    }
  };

  // Function to reset card to normal state
  const resetCardState = () => {
    setCardState('normal');
    setCardHeight(40);
  };

  // Drag handling functions
  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragStartY(e.touches ? e.touches[0].clientY : e.clientY);
    setDragCurrentY(e.touches ? e.touches[0].clientY : e.clientY);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = currentY - dragStartY; // Inverted for bottom positioning
    const newHeight = Math.max(20, Math.min(80, 40 - (deltaY / window.innerHeight) * 100));
    
    setCardHeight(newHeight);
    setDragCurrentY(currentY);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Determine final state based on height
    if (cardHeight > 60) {
      setCardState('expanded');
      setCardHeight(75);
    } else if (cardHeight < 30) {
      setCardState('collapsed');
      setCardHeight(20);
    } else {
      setCardState('normal');
      setCardHeight(40);
    }
  };

  // Prevent default touch behavior
  const preventDefault = (e) => {
    e.preventDefault();
  };

  // Function to clear point highlight and markers
  const clearHighlight = () => {
    setHighlightedPoint(null);
    setVisiblePoints(new Set());
    setMapHighlighted(false); // Also clear map highlight
    setSelectedPointDetails(null); // Clear mobile card
    
    // On mobile, close the points panel when clearing highlights
    if (isMobile) {
      setShowFilter(false);
    }
    
    // Remove specific point markers and clear highlights
    markersRef.current = markersRef.current.filter(marker => {
      if (marker._isSpecificPoint) {
        if (marker && marker.remove) {
          marker.remove();
        }
        return false; // Remove from array
      } else {
        // Remove highlight from regular markers (only if they have getElement method)
        if (marker && typeof marker.getElement === 'function') {
          const markerElement = marker.getElement();
          if (markerElement) {
            markerElement.classList.remove('highlighted-point');
            markerElement.style.animation = '';
            markerElement.style.zIndex = '';
          }
        }
        return true; // Keep in array
      }
    });
  };

  // Function to close mobile card only (without clearing markers)
  const closeMobileCardOnly = () => {
    setSelectedPointDetails(null);
    setCardState('normal');
    setShowMobileDetails(false);
    // Don't clear highlights or markers - just close the card
  };

  // Function to collect all trip points for filter
  const collectAllTripPoints = (vehicleData) => {
    const points = [];
    
    vehicleData.forEach(vehicle => {
      if (vehicle.tripData && vehicle.tripData.points && vehicle.tripData.points.length > 0) {
        vehicle.tripData.points.forEach((point, index) => {
          const pointData = {
            id: point.id || `P${String(index + 1).padStart(3, '0')}`,
            tripId: vehicle.tripData.terminalId,
            trip_id: point.trip_id,
            tripName: vehicle.name,
            latitude: point.latitude,
            longitude: point.longitude,
            speed: point.speed || 0,
            pointTime: point.point_time,
            point_time: point.point_time,
            sequence: index + 1,
            totalPoints: vehicle.tripData.points.length,
            isStart: index === 0,
            isEnd: index === vehicle.tripData.points.length - 1,
            color: getMarkerColor(index, vehicle.tripData.points.length),
            duration: vehicle.tripData.duration,
            distance: vehicle.tripData.distance,
            terminal_id: point.terminal_id || vehicle.tripData.terminalId
          };
          
          points.push(pointData);
        });
      }
    });
    
    return points;
  };

  // Function to create custom marker icons
  // const createMarkerIcon = (type, pointIndex, speed = 0) => {
  //   let color = '#22c55e';
  //   let size = 32;
    
  //   switch(type) {
  //     case 'start':
  //       color = '#22c55e'; // Green for start
  //       size = 50;
  //       break;
  //     case 'end':
  //       color = '#ef4444'; // Red for end
  //       size = 50;
  //       break;
  //     case 'fast':
  //       color = '#f59e0b'; // Orange for fast movement (>20 km/h)
  //       size = 40;
  //       break;
  //     case 'normal':
  //       color = '#22c55e'; // Green for normal movement (10-20 km/h)
  //       size = 35;
  //       break;
  //     case 'stopped':
  //       color = '#6b7280'; // Gray for stopped (<10 km/h)
  //       size = 30;
  //       break;
  //     case 'slow':
  //       color = '#22c55e'; // Green for slow movement (5-10 km/h)
  //       size = 32;
  //       break;
  //     case 'very_slow':
  //       color = '#22c55e'; // Green for very slow movement (1-5 km/h)
  //       size = 30;
  //       break;
  //   }
    
  //   // Create a simple circle icon that's more compatible
  //   const iconSvg = `
  //     <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
  //       <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="${color}" stroke="white" stroke-width="3"/>
  //       <text x="${size/2}" y="${size/2 + 4}" text-anchor="middle" font-size="${size * 0.3}" fill="white" font-weight="bold">
  //         ${type === 'start' ? 'S' : 
  //           type === 'end' ? 'E' : 
  //           type === 'fast' ? 'F' : 
  //           type === 'normal' ? 'N' :
  //           type === 'slow' ? 'L' :
  //           type === 'very_slow' ? 'V' :
  //           type === 'stopped' ? 'P' : 'N'}
  //       </text>
  //     </svg>
  //   `;
    
  //   return {
  //     url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(iconSvg)}`,
  //     scaledSize: { width: size, height: size },
  //     anchor: { x: size/2, y: size/2 }
  //   };
  // };

  // Function to fit map bounds to show all markers
  const fitMapToMarkers = (map, vehicleData) => {
    try {
      if (!vehicleData || vehicleData.length === 0) {
        return;
      }

      let allPoints = [];
      
      vehicleData.forEach(vehicle => {
        if (vehicle.tripData && vehicle.tripData.points && vehicle.tripData.points.length > 0) {
          // Add all trip points
          vehicle.tripData.points.forEach(point => {
            allPoints.push({
              lat: parseFloat(point.latitude),
              lng: parseFloat(point.longitude)
            });
          });
        } else {
          // Add single vehicle position
          allPoints.push({
            lat: parseFloat(vehicle.position[0]),
            lng: parseFloat(vehicle.position[1])
          });
        }
      });

      if (allPoints.length === 0) {
        return;
      }

      // Calculate bounds
      const lats = allPoints.map(p => p.lat);
      const lngs = allPoints.map(p => p.lng);
      
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Add some padding
      const latPadding = (maxLat - minLat) * 0.1;
      const lngPadding = (maxLng - minLng) * 0.1;

      // Create bounds in the correct format for Mappls
      const bounds = [
        [minLng - lngPadding, minLat - latPadding], // Southwest corner [lng, lat]
        [maxLng + lngPadding, maxLat + latPadding]  // Northeast corner [lng, lat]
      ];

      
      // Fit the map to the bounds
      map.fitBounds(bounds, {
        padding: 100, // Increased padding around the bounds
        maxZoom: 16, // Reduced max zoom to ensure all markers are visible
        duration: 1000 // Add smooth animation
      });

    } catch (error) {
    }
  };

  // Update markers on map with live data
  const updateMapMarkers = (map, vehicleData, fullTripData = null) => {
    try {
      
      // Use fullTripData if provided, otherwise fall back to selectedTrip
      const trip = fullTripData?.trip || selectedTrip?.trip || null;
      
      // Clear existing markers
      markersRef.current.forEach(marker => {
        if (marker && marker.remove) {
          marker.remove();
        }
      });
      markersRef.current = [];

      if (!vehicleData || vehicleData.length === 0) {
        return;
      }

      vehicleData.forEach((vehicle, index) => {
        try {
          const isMainLocation = vehicle.position[0] === 17.44074950505652 && 
                               vehicle.position[1] === 78.3998802562579;
          
          // Check if this is trip data
          const isTripData = vehicle.tripData;
          
          // If this is trip data and has points, show the complete trip flow
          if (isTripData && vehicle.tripData.points && vehicle.tripData.points.length > 0) {
            
            // Add vehicle markers for each point in the trip
            
            // Create route coordinates for the trip
            const routeCoordinates = vehicle.tripData.points.map(point => ({
              lat: point.latitude,
              lng: point.longitude
            }));
            
            // Create markers only for start and end points by default
            routeCoordinates.forEach((coord, i) => {
              const point = vehicle.tripData.points[i];
              const isStartPoint = i === 0;
              const isEndPoint = i === routeCoordinates.length - 1;
              
              // Only show start and end points by default
              if (!isStartPoint && !isEndPoint) {
                return; // Skip waypoints
              }
              
              // Generate custom point ID using the actual point ID from data
              const pointId = point.id || `P${String(i + 1).padStart(3, '0')}`;
              const customId = `TRIP-${vehicle.tripData.terminalId}-P${pointId}`;
              
              // Determine marker title
              let markerTitle = `${customId} - WayPoint ${i + 1}`;
              if (isStartPoint) {
                markerTitle = 'Trip Start - Vehicle';
              } else if (isEndPoint) {
                markerTitle = 'Trip End - Vehicle';
              }
              
              try {
                
                // Use regular brown Mappls marker
                const marker = new window.mappls.Marker({
                  map: map,
                  position: {
                    lat: coord.lat,
                    lng: coord.lng
                  },
                  popupHtml: `
                    <div style="padding: 8px; min-width: 320px; max-height: 400px; overflow-y: auto;">
                      <h3 style="margin: 0 0 12px 0; color: #1f2937; font-size: 14px; font-weight: 600; border-bottom: 2px solid ${isStartPoint ? '#22c55e' : isEndPoint ? '#ef4444' : '#3b82f6'}; padding-bottom: 8px;">
                        ${markerTitle}
                      </h3>
                      
                      <!-- Trip Information -->
                      <div style="margin-bottom: 12px; padding: 8px; background: #f0f9ff; border-left: 3px solid #3b82f6; border-radius: 4px;">
                        <p style="margin: 4px 0; font-size: 11px; color: #1e40af; font-weight: 600;">TRIP INFORMATION</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Trip ID:</b> ${trip?.id || point.trip_id || vehicle.tripData.terminalId || 'N/A'}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Status:</b> ${trip?.trip_status || 'N/A'}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Start:</b> ${trip?.trip_start_time ? formatDateTime(trip.trip_start_time) : 'N/A'}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>End:</b> ${trip?.trip_end_time ? formatDateTime(trip.trip_end_time) : 'N/A'}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Duration:</b> ${trip?.duration_minutes || vehicle.tripData.duration || 'N/A'} min</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Distance:</b> ${trip?.distance_km ? trip.distance_km.toFixed(2) : (vehicle.tripData.distance ? vehicle.tripData.distance.toFixed(2) : 'N/A')} km</p>
                        ${trip?.created_at ? `<p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Created:</b> ${formatDate(trip.created_at)}</p>` : ''}
                      </div>

                      <!-- Trip Coordinates -->
                      <div style="margin-bottom: 12px; padding: 8px; background: #faf5ff; border-left: 3px solid #a855f7; border-radius: 4px;">
                        <p style="margin: 4px 0; font-size: 11px; color: #6b21a8; font-weight: 600;">TRIP COORDINATES</p>
                        ${trip?.start_lat ? `<p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Start:</b> ${trip.start_lat.toFixed(6)}, ${trip.start_lng.toFixed(6)}</p>` : ''}
                        ${trip?.end_lat ? `<p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>End:</b> ${trip.end_lat.toFixed(6)}, ${trip.end_lng.toFixed(6)}</p>` : ''}
                      </div>

                      <!-- Point Information -->
                      <div style="margin-bottom: 12px; padding: 8px; background: #f9fafb; border-left: 3px solid ${isStartPoint ? '#22c55e' : isEndPoint ? '#ef4444' : '#6b7280'}; border-radius: 4px;">
                        <p style="margin: 4px 0; font-size: 11px; color: #374151; font-weight: 600;">WayPoint INFORMATION</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>WayPoint ID:</b> ${pointId}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Terminal ID:</b> ${point.terminal_id || trip?.device_id || vehicle.tripData.terminalId || 'N/A'}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Speed:</b> ${point.speed || 0} km/h</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Point Time:</b> ${point.point_time ? formatDateTime(point.point_time) : 'N/A'}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #374151;"><b>Sequence:</b> ${i + 1} of ${vehicle.tripData.points.length}</p>
                        <p style="margin: 2px 0; font-size: 11px; color: #6b7280;"><b>Coords:</b> ${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}</p>
                        ${isStartPoint ? '<p style="margin: 2px 0; color: #3b82f6;"><strong>🚀 Trip Start - Vehicle Location</strong></p>' : ''}
                        ${isEndPoint ? '<p style="margin: 2px 0; color: #ef4444;"><strong>🏁 Trip End - Vehicle Location</strong></p>' : ''}
                      </div>
                    </div>
                  `,
                  fitbounds: false
                });
                
                if (marker) {
                  // Store point ID and trip ID for highlighting
                  marker._pointId = pointId;
                  marker._tripId = vehicle.tripData.terminalId;
                  marker._isSpecificPoint = false; // Mark as regular start/end point
                  marker._fullPointData = point; // Store full point data
                  marker._fullTripData = vehicle.tripData; // Store full trip data
                  markersRef.current.push(marker);
                  
                  // Add click event to open mobile card
                  marker.addListener("click", () => {
                    const map = mapInstance.current;
                    if (map?.panTo) map.panTo({ lat: coord.lat, lng: coord.lng }, { duration: 800 });
                    
                    // Open mobile card with point details
                    if (isMobile) {
                      const detailedPoint = {
                        id: pointId,
                        tripId: vehicle.tripData.terminalId,
                        trip_id: point.trip_id || vehicle.tripData.terminalId,
                        tripName: vehicle.name,
                        latitude: point.latitude,
                        longitude: point.longitude,
                        speed: point.speed,
                        pointTime: point.point_time,
                        point_time: point.point_time,
                        sequence: i + 1,
                        totalPoints: vehicle.tripData.points.length,
                        isStart: isStartPoint,
                        isEnd: isEndPoint,
                        color: getMarkerColor(i, vehicle.tripData.points.length),
                        duration: trip?.duration_minutes || vehicle.tripData.duration,
                        distance: trip?.distance_km || vehicle.tripData.distance,
                        tripStartTime: trip?.trip_start_time,
                        tripEndTime: trip?.trip_end_time,
                        tripStatus: trip?.trip_status,
                        terminalId: trip?.device_id || vehicle.tripData.terminalId,
                        terminal_id: point.terminal_id || trip?.device_id || vehicle.tripData.terminalId,
                        start_lat: trip?.start_lat,
                        start_lng: trip?.start_lng,
                        end_lat: trip?.end_lat,
                        end_lng: trip?.end_lng,
                        created_at: trip?.created_at
                      };
                      setSelectedPointDetails(detailedPoint);
                      setShowFilter(false);
                      setCardState('normal');
                      setCardHeight(40);
                      setShowMobileDetails(false);
                    }
                  });
                  
                  // Debug: Check if marker is visible
                  setTimeout(() => {
                    const markerElement = marker.getElement();
                    if (markerElement) {
                    } else {
                    }
                  }, 1000);
                } else {
                }
              } catch (markerError) {
              }
            });
            
            // Draw polyline for trip route
            if (vehicle.tripData.points.length > 1) {
              try {
                
                 const polyline = new window.mappls.Polyline({
                   map: map,
                   path: routeCoordinates,
                   strokeColor: '#0000FF',
                   strokeOpacity: 0.8,
                   strokeWeight: 8,
                   fitbounds: false,
                  popupHtml: `
                    <div style="padding: 8px;">
                      <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 14px; font-weight: 600;">
                        Trip Route
                      </h3>
                      <p style="margin: 2px 0; font-size: 12px; color: #6b7280;">
                        <strong>Total Points:</strong> ${vehicle.tripData.points.length}
                      </p>
                      <p style="margin: 2px 0; font-size: 12px; color: #6b7280;">
                        <strong>Duration:</strong> ${vehicle.tripData.duration || 'N/A'} minutes
                      </p>
                      <p style="margin: 2px 0; font-size: 12px; color: #6b7280;">
                        <strong>Distance:</strong> ${vehicle.tripData.distance ? vehicle.tripData.distance.toFixed(2) : 'N/A'} km
                      </p>
                    </div>
                  `
                });
                
                markersRef.current.push(polyline);
                
                // Skip adding vehicle icons along route - only show start and end points
                
              } catch (polylineError) {
              }
            }
          } else {
            // Single vehicle marker (non-trip data)
            
            
            // const markerIcon = createMarkerIcon(
            //   vehicle.status === 'idle' ? 'stopped' : 'normal',
            //   0,
            //   0
            // );
            
            // Use regular brown Mappls marker for single vehicle
            const marker = new window.mappls.Marker({
              map: map,
              position: {
                lat: vehicle.position[0],
                lng: vehicle.position[1]
              },
              popupHtml: `
                <div style="padding: 8px; min-width: 200px;">
                  <h3 style="margin: 0 0 8px 0; color: #1f2937; font-size: 14px; font-weight: 600;">
                    ${vehicle.name}
                  </h3>
                  <div style="font-size: 12px; color: #6b7280;">
                    <p style="margin: 2px 0;"><strong>Status:</strong> ${vehicle.status}</p>
                    <p style="margin: 2px 0;"><strong>Driver:</strong> ${vehicle.driver}</p>
                    <p style="margin: 2px 0;"><strong>Speed:</strong> ${vehicle.speed}</p>
                    <p style="margin: 2px 0;"><strong>Battery:</strong> ${vehicle.battery}%</p>
                    <p style="margin: 2px 0;"><strong>Fuel:</strong> ${vehicle.fuel}%</p>
                    <p style="margin: 2px 0; font-size: 11px; color: #9ca3af;"><strong>Last Seen:</strong> ${new Date(vehicle.lastSeen).toLocaleString()}</p>
                  </div>
                </div>
              `,
              fitbounds: false
            });
            
            markersRef.current.push(marker);
          }
        } catch (vehicleError) {
        }
      });

      
      // Collect all trip points for filter
      const points = collectAllTripPoints(vehicleData);
      setAllTripPoints(points);
      
      // Add a small delay to ensure all markers are rendered before fitting bounds
      setTimeout(() => {
        fitMapToMarkers(map, vehicleData);
      }, 1000);
      
    } catch (error) {
      toast.error('Failed to update markers on map');
    }
  };

  // Fetch live data
  const fetchLiveData = async (map) => {
    try {
      
      let response;
      
      if (!tripId) {
        toast.warning("No tripId found in URL", {
          position: isMobile ? "top-center" : "top-right",
          className: isMobile ? "toast-mobile" : "",
          bodyClassName: isMobile ? "toast-mobile-body" : "",
          progressClassName: isMobile ? "toast-mobile-progress" : "",
        });
        return;
      }
      if (tripId) {
        // 🔹 Fetch single trip data
        response = await get(`/trips/${tripId}`);

        if (!response?.trip || !response?.points?.length) {
            toast.error("Trip data not found", {
          position: isMobile ? "top-center" : "top-right",
          className: isMobile ? "toast-mobile" : "",
          bodyClassName: isMobile ? "toast-mobile-body" : "",
          progressClassName: isMobile ? "toast-mobile-progress" : "",
        });
          return;
        }

        if (response && response.trip && response.points) {
          setSelectedTrip(response);

          // Transform trip data for map display
          const tripData = {
            name: `Trip ${response.trip.id}`,
            position: [
              parseFloat(response.trip.end_lat),
              parseFloat(response.trip.end_lng)
            ],
            tripData: {
              terminalId: response.trip.terminal_id,
              points: response.points || [],
              duration: response.trip.duration_minutes,
              distance: response.trip.distance_km
            }
          };

          // 🗺️ Draw route or markers on map - pass full trip data
          updateMapMarkers(map, [tripData], response);
          setVehicles([tripData]);

              // ✅ Show Start and End markers immediately on load - pass full trip data
          showTripPoints(tripData.tripData, tripId, response);

          // ✅ Auto-display Start and End markers - pass trip data
          const { points } = response;
          const startPoint = points.find((p) => p.isStart);
          const endPoint = points.find((p) => p.isEnd);

          if (startPoint) showSpecificPointOnMap(startPoint, tripId, response);
          if (endPoint) showSpecificPointOnMap(endPoint, tripId, response);
          // ✅ Optional: Fit map bounds to start–end
          if (map && startPoint && endPoint) {
            const bounds = new window.mappls.LatLngBounds();
            bounds.extend({
              lat: parseFloat(startPoint.latitude),
              lng: parseFloat(startPoint.longitude),
            });
            bounds.extend({
              lat: parseFloat(endPoint.latitude),
              lng: parseFloat(endPoint.longitude),
            });
            map.fitBounds(bounds, { padding: 60 });
          }
          setLastUpdate(new Date());

        }
      } else {
        // 🔹 Fetch all trips (when no tripId param)
        response = await get(
          "https://api.trackyourwheelz.com/api/trips?terminal_id=0867440066536178"
        );

        if (response && response.success && response.data?.length > 0) {

          // Transform trips for map display
          const vehiclesData = response.data.map((trip) => ({
            name: `Trip ${trip._id}`,
            position: trip.endCoords
              ? [parseFloat(trip.endCoords.lat), parseFloat(trip.endCoords.lng)]
              : [17.44074950505652, 78.3998802562579],
            tripData: {
              terminalId: trip.terminalId,
              points: trip.points || [],
              duration: trip.duration_minutes || trip.duration,
              distance: trip.distance_km || trip.distance,
            },
          }));

          updateMapMarkers(map, vehiclesData);
          setVehicles(vehiclesData);

          // ✅ Auto markers for all trips (optional)
          vehiclesData.forEach((vehicle) => {
            const { points } = vehicle.tripData;
            const startPoint = points.find((p) => p.isStart);
            const endPoint = points.find((p) => p.isEnd);

            if (startPoint) showSpecificPointOnMap(startPoint, vehicle.name);
            if (endPoint) showSpecificPointOnMap(endPoint, vehicle.name);
          });

        } else {
        }
      }

      setLastUpdate(new Date());
    } catch (error) {
      toast.error("Failed to fetch live tracking data", {
        position: isMobile ? "top-center" : "top-right",
        className: isMobile ? "toast-mobile" : "",
        bodyClassName: isMobile ? "toast-mobile-body" : "",
        progressClassName: isMobile ? "toast-mobile-progress" : "",
      });
    }
  };

  // Start live tracking
  const startLiveTracking = () => {
    if (intervalRef.current) return;
    
    setIsTracking(true);
    toast.success('Live tracking started', {
      position: isMobile ? "top-center" : "top-right",
      className: isMobile ? "toast-mobile" : "",
      bodyClassName: isMobile ? "toast-mobile-body" : "",
      progressClassName: isMobile ? "toast-mobile-progress" : "",
    });
    
    // Fetch immediately
    if (mapInstance.current) {
      fetchLiveData(mapInstance.current);
    }
    
    // Set up interval for updates every 10 seconds
    intervalRef.current = setInterval(() => {
      if (mapInstance.current) {
        fetchLiveData(mapInstance.current);
      }
    }, 10000);
  };

  // Stop live tracking
  const stopLiveTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
    toast.info('Live tracking stopped', {
      position: isMobile ? "top-center" : "top-right",
      className: isMobile ? "toast-mobile" : "",
      bodyClassName: isMobile ? "toast-mobile-body" : "",
      progressClassName: isMobile ? "toast-mobile-progress" : "",
    });
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    
    if (!document.fullscreenElement) {
      // Enter fullscreen - use the map container instead of just the map div
      const mapContainer = mapRef.current?.parentElement;
      if (mapContainer?.requestFullscreen) {
        mapContainer.requestFullscreen().then(() => {
          setIsFullscreen(true);
          // Trigger map resize after entering fullscreen
          setTimeout(() => {
            if (mapInstance.current && mapInstance.current.resize) {
              mapInstance.current.resize();
            }
          }, 300);
        }).catch(err => {
          toast.error('Failed to enter fullscreen mode');
        });
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
          
          // Force reset controls position after fullscreen exits
          const resetControlsPosition = () => {
            if (mapRef.current?.parentElement) {
              const container = mapRef.current.parentElement;
              const controls = container.querySelector('.map-controls-container');
              const panel = container.querySelector('.map-points-panel');
              
              
              // Remove all inline styles to let CSS handle positioning
              if (controls) {
                // Log before reset
                const beforeStyles = window.getComputedStyle(controls);
                
                
                controls.removeAttribute('style');
                // Explicitly set positioning to override any computed styles
                controls.style.position = 'absolute';
                controls.style.top = '1rem';
                controls.style.right = '1rem';
                controls.style.left = 'auto';
                controls.style.bottom = 'auto';
                controls.style.transform = 'none';
                controls.style.translate = 'none';
                controls.style.zIndex = '20';
                // Force reflow to ensure CSS is reapplied
                void controls.offsetHeight;
                // Force a repaint
                controls.style.display = 'none';
                void controls.offsetHeight;
                controls.style.display = 'flex';
                
                // Log after reset
                const afterStyles = window.getComputedStyle(controls);
                const rect = controls.getBoundingClientRect();
                
              }
              
              if (panel) {
                panel.removeAttribute('style');
                // Explicitly set positioning to override any computed styles
                panel.style.position = 'absolute';
                panel.style.top = '1rem';
                panel.style.left = '1rem';
                panel.style.right = 'auto';
                panel.style.bottom = 'auto';
                panel.style.transform = 'none';
                panel.style.translate = 'none';
                panel.style.zIndex = '20';
                // Force reflow to ensure CSS is reapplied
                void panel.offsetHeight;
              }
              
              // Reset container styles
              container.style.position = '';
              container.style.width = '';
              container.style.height = '';
              container.style.transform = '';
              container.style.translate = '';
              
              // Force a reflow on the container
              void container.offsetHeight;
            }
          };
          
          // Reset immediately and multiple times to ensure it sticks
          resetControlsPosition();
          
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            resetControlsPosition();
            
            // Reset again after a short delay
            setTimeout(() => {
              resetControlsPosition();
              if (mapInstance.current && mapInstance.current.resize) {
                mapInstance.current.resize();
              }
              window.dispatchEvent(new Event('resize'));
            }, 50);
          });
        }).catch(err => {
          setIsFullscreen(false);
        });
      }
    }
  };

  useEffect(() => {
    let timeoutId;
    let intervalId;
    let scriptLoaded = false;

    // Detect mobile device
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    // Initialize mobile detection
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Handle map resize when layout changes (e.g., sidebar collapse/expand)
    const handleLayoutResize = () => {
      if (mapInstance.current && mapInstance.current.resize) {
        // Use requestAnimationFrame to ensure layout has settled
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (mapInstance.current && mapInstance.current.resize) {
              mapInstance.current.resize();
            }
          }, 150);
        });
      }
    };
    
    // Listen for window resize events (triggers when sidebar collapses/expands)
    window.addEventListener('resize', handleLayoutResize);
    
    // Also listen for sidebar state changes via MutationObserver
    let sidebarObserver = null;
    const setupSidebarObserver = () => {
      // Find sidebar wrapper element (the one with data-state attribute)
      const sidebarWrapper = document.querySelector('[data-slot="sidebar-wrapper"]');
      const sidebarElement = document.querySelector('[data-slot="sidebar"]');
      
      const targetElement = sidebarWrapper || sidebarElement;
      
      if (targetElement && window.MutationObserver) {
        sidebarObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            // Check if data-state or data-collapsible attribute changed
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'data-state' || mutation.attributeName === 'data-collapsible')) {
              
              
              // Trigger map resize when sidebar state changes
              // Use multiple timeouts to catch the animation at different stages
              setTimeout(() => {
                handleLayoutResize();
              }, 100);
              setTimeout(() => {
                handleLayoutResize();
              }, 300); // After sidebar animation completes
              setTimeout(() => {
                handleLayoutResize();
              }, 500); // Final check
            }
          });
        });
        
        // Observe both attribute changes and subtree changes
        sidebarObserver.observe(targetElement, {
          attributes: true,
          attributeFilter: ['data-state', 'data-collapsible'],
          subtree: true
        });
      } else {
        // Retry if element not found
        setTimeout(setupSidebarObserver, 500);
      }
    };
    
    // Set up sidebar observer after a short delay to ensure DOM is ready
    setTimeout(setupSidebarObserver, 1000);
    
    // ResizeObserver will be set up after map loads (see map.on('load') handler)
    let resizeObserver = null;

    // Add global mouse event listeners for drag functionality
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleDragMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    // Add event listeners
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchmove', handleGlobalMouseMove, { passive: false });
    document.addEventListener('touchend', handleGlobalMouseUp);

    // Handle fullscreen change events
    const handleFullscreenChange = () => {
      
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
        
        // Force reset controls position after fullscreen exits
        const resetControlsPosition = () => {
          if (mapRef.current?.parentElement) {
            const container = mapRef.current.parentElement;
            const controls = container.querySelector('.map-controls-container');
            const panel = container.querySelector('.map-points-panel');
            
            
            // Remove all inline styles to let CSS handle positioning
            if (controls) {
              // Log before reset
              const beforeStyles = window.getComputedStyle(controls);
              
              
              controls.removeAttribute('style');
              // Explicitly set positioning to override any computed styles
              controls.style.position = 'absolute';
              controls.style.top = '1rem';
              controls.style.right = '1rem';
              controls.style.left = 'auto';
              controls.style.bottom = 'auto';
              controls.style.transform = 'none';
              controls.style.translate = 'none';
              controls.style.zIndex = '20';
              // Force reflow to ensure CSS is reapplied
              void controls.offsetHeight;
              // Force a repaint
              controls.style.display = 'none';
              void controls.offsetHeight;
              controls.style.display = 'flex';
              
              // Log after reset
              const afterStyles = window.getComputedStyle(controls);
              const rect = controls.getBoundingClientRect();
              
            }
            
            if (panel) {
              panel.removeAttribute('style');
              // Explicitly set positioning to override any computed styles
              panel.style.position = 'absolute';
              panel.style.top = '1rem';
              panel.style.left = '1rem';
              panel.style.right = 'auto';
              panel.style.bottom = 'auto';
              panel.style.transform = 'none';
              panel.style.translate = 'none';
              panel.style.zIndex = '20';
              // Force reflow to ensure CSS is reapplied
              void panel.offsetHeight;
            }
            
            // Reset container styles
            container.style.position = '';
            container.style.width = '';
            container.style.height = '';
            container.style.transform = '';
            container.style.translate = '';
            
            // Force a reflow on the container
            void container.offsetHeight;
          }
        };
        
        // Reset immediately and multiple times to ensure it sticks
        resetControlsPosition();
        
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          resetControlsPosition();
          
          // Reset again after a short delay
          setTimeout(() => {
            resetControlsPosition();
            if (mapInstance.current && mapInstance.current.resize) {
              mapInstance.current.resize();
            }
            window.dispatchEvent(new Event('resize'));
          }, 50);
        });
      } else {
        setIsFullscreen(true);
        // Trigger map resize after entering fullscreen
        setTimeout(() => {
          if (mapInstance.current && mapInstance.current.resize) {
            mapInstance.current.resize();
          }
        }, 300);
      }
    };

    // Add event listeners for fullscreen changes (cross-browser support)
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Remove complex error handling that might cause issues

    const checkMapplsLoaded = () => {
      const isLoaded = window.mappls && 
             window.mappls.Map && 
             window.mappls.Marker && 
             window.mappls.Polyline;
      
      if (isLoaded) {
        
      }
      
      return isLoaded;
    };

    const initializeMap = async () => {
      try {
        
        // Wait for Mappls to load
        const maxAttempts = 50;
        let attempts = 0;
        
        const waitForMappls = () => {
          return new Promise((resolve, reject) => {
            intervalId = setInterval(() => {
              attempts++;
              
              if (checkMapplsLoaded()) {
                clearInterval(intervalId);
                scriptLoaded = true;
                resolve();
              } else if (attempts >= maxAttempts) {
                clearInterval(intervalId);
                reject(new Error('Mappls SDK failed to load after maximum attempts'));
              }
            }, 100);
          });
        };

        await waitForMappls();

        if (!mapRef.current) {
          throw new Error('Map container not found');
        }

        
        // Try creating map with minimal options first
        let map;
        try {
          map = new window.mappls.Map(mapRef.current, {
            center: [17.44074950505652, 78.3998802562579],
            zoom: 12,
            zoomControl: true,
            location: true,
            scrollWheel: true,
            scrollZoom: true,
            draggable: true,
            hybrid: false,
            clickableIcons: true,
            backgroundColor: '#f0f0f0',
            geolocation: false
          });
        } catch (mapError) {
          
          // Fallback to minimal options
          try {
            map = new window.mappls.Map(mapRef.current, {
              center: [17.44074950505652, 78.3998802562579],
              zoom: 12
            });
          } catch (minimalError) {
            throw new Error(`Map initialization failed: ${minimalError.message || 'Unknown error'}`);
          }
        }

        mapInstance.current = map;

        // Wait for map to be ready
        map.on('load', () => {
          setMapLoaded(true);
          setIsLoading(false);
          
          
          // Enable infinite scroll and zoom after map loads
          try {
            // Enable infinite scrolling
            if (map.setOptions) {
              map.setOptions({
                scrollWheel: true,
                scrollZoom: true,
                draggable: true,
                gestureHandling: 'greedy'
              });
            }
            
            // Add custom zoom controls for infinite zoom
            if (map.zoomControl) {
              map.zoomControl.setOptions({
                position: 'top-right'
              });
            }
            
          } catch (zoomError) {
          }
          
          // Set up ResizeObserver after map is loaded
          setTimeout(() => {
            if (mapRef.current) {
              const mapContainer = mapRef.current.parentElement;
              if (mapContainer && window.ResizeObserver) {
                resizeObserver = new ResizeObserver(() => {
                  if (mapInstance.current && mapInstance.current.resize) {
                    requestAnimationFrame(() => {
                      setTimeout(() => {
                        if (mapInstance.current && mapInstance.current.resize) {
                          mapInstance.current.resize();
                        }
                      }, 100);
                    });
                  }
                });
                resizeObserver.observe(mapContainer);
              }
            }
          }, 500);
          
          // Fetch initial data
          fetchLiveData(map);
        });

        // Handle map errors
        map.on('error', (e) => {
          setError('Map initialization error: ' + (e.message || 'Unknown error'));
          setIsLoading(false);
        });

        // Set a timeout in case 'load' event doesn't fire
        timeoutId = setTimeout(() => {
          if (!mapLoaded) {
            setMapLoaded(true);
            setIsLoading(false);
            
            // Fetch initial data
            fetchLiveData(map);
          }
        }, 5000);

      } catch (err) {
        setError(err.message || 'Failed to initialize map');
        setIsLoading(false);
        
        // Add a fallback message in the map container
        if (mapRef.current) {
          mapRef.current.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666; text-align: center; padding: 20px;">
              <h3 style="margin-bottom: 10px; color: #333;">Map Loading Failed</h3>
              <p style="margin-bottom: 15px;">Unable to initialize the map. Please check your internet connection and try refreshing the page.</p>
              <button onclick="window.location.reload()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
                Refresh Page
              </button>
            </div>
          `;
        }
      }
    };

    // Start initialization
    initializeMap();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
      // Stop animation on unmount
      if (isAnimatingRef.current) {
        stopAnimation();
      }
      // Cancel animation frame if still running
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('resize', handleLayoutResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (sidebarObserver) {
        sidebarObserver.disconnect();
      }
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalMouseMove);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  const handleRefresh = () => {
    setMapLoaded(false);
    setError(null);
    setIsLoading(true);
    
    // Reinitialize the map
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Toggle animation button - show speed controls
  const toggleAnimationControls = () => {
    if (isAnimating) {
      stopAnimation();
    } else {
      setShowSpeedControls(!showSpeedControls);
    }
  };

  // Stop animation
  const stopAnimation = () => {
    isAnimatingRef.current = false;
    setIsAnimating(false);
    setShowSpeedControls(false);
    setCurrentAnimationIndex(0);
    
    // Cancel animation frame
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    
    // Clear animation interval (if any)
    if (animationIntervalRef.current) {
      clearTimeout(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    
    // Remove animated marker
    if (animationMarkerRef.current) {
      try {
        if (animationMarkerRef.current.remove) {
          animationMarkerRef.current.remove();
        }
      } catch (error) {
      }
      animationMarkerRef.current = null;
    }
  };

  // Get RED car icon SVG (vertical, rotation-friendly)
const getCarIconRed = () => {
  const CarIcon = `
  <svg width="64" height="128" viewBox="0 0 300 600" xmlns="http://www.w3.org/2000/svg">

    <!-- Car body -->
    <rect x="80" y="120" width="140" height="300" rx="40" fill="#dc2626"/>

    <!-- Roof -->
    <rect x="105" y="70" width="90" height="70" rx="30" fill="#b91c1c"/>

    <!-- Windshield -->
    <rect x="110" y="90" width="80" height="40" rx="10" fill="#e0f2fe"/>

    <!-- Rear window -->
    <rect x="110" y="300" width="80" height="50" rx="10" fill="#e0f2fe"/>

    <!-- Headlights -->
    <rect x="100" y="110" width="30" height="10" rx="5" fill="#fde047"/>
    <rect x="170" y="110" width="30" height="10" rx="5" fill="#fde047"/>

    <!-- Tail lights -->
    <rect x="100" y="400" width="30" height="10" rx="5" fill="#7f1d1d"/>
    <rect x="170" y="400" width="30" height="10" rx="5" fill="#7f1d1d"/>

    <!-- Left wheels -->
    <rect x="60" y="170" width="20" height="60" rx="8" fill="#111827"/>
    <rect x="60" y="310" width="20" height="60" rx="8" fill="#111827"/>

    <!-- Right wheels -->
    <rect x="220" y="170" width="20" height="60" rx="8" fill="#111827"/>
    <rect x="220" y="310" width="20" height="60" rx="8" fill="#111827"/>

    <!-- Center direction line -->
    <line x1="150" y1="60" x2="150" y2="460"
          stroke="#ffffff"
          stroke-width="4"
          stroke-dasharray="12,12"
          opacity="0.6"/>

  </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CarIcon)}`;
};

  // Helper function: Calculate distance between two points (Haversine formula)
  const getDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // Helper function: Calculate bearing between two points
  const calculateBearing = (lat1, lng1, lat2, lng2) => {
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = 
      Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
    const bearing = Math.atan2(y, x) * 180 / Math.PI;
    return (bearing + 360) % 360; // Normalize to 0-360
  };

  // Helper function: Linear interpolation for angles (handles wrap-around)
  const lerpAngle = (current, target, factor) => {
    let diff = target - current;
    // Normalize difference to -180 to 180 range
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return current + diff * factor;
  };

  // Helper function: Check if marker has setRotation method
  const hasSetRotation = (marker) => {
    return marker && typeof marker.setRotation === 'function';
  };

  // Create animated truck marker
  const createAnimationMarker = (lat, lng) => {
    if (!mapInstance.current) return null;

    try {
      // Use truck SVG icon
      const truckIcon = getCarIconRed();

      const marker = new window.mappls.Marker({
        map: mapInstance.current,
        position: { lat, lng },
        icon: truckIcon,
        width: 30,
        height: 60,
        fitbounds: false,
        anchor: { x: 15, y: 60 }, // Anchor at bottom center of truck
        zIndex: 1000 // High z-index to appear above other markers
      });

      // Add smooth movement animation via CSS after marker is created
      setTimeout(() => {
        try {
          const markerElement = marker.getElement?.();
          if (markerElement) {
            markerElement.style.transition = 'transform 0.1s ease-out';
            markerElement.style.zIndex = '1000';
            markerElement.classList.add('animation-car-marker'); // Keep class name for CSS compatibility
          }
        } catch (error) {
        }
      }, 100);

      return marker;
    } catch (error) {
      return null;
    }
  };

  // Start truck animation through waypoints
  const startAnimation = (speed = 'fast') => {
    if (!mapInstance.current || !mapLoaded || allTripPoints.length < 2) {
      toast.error('No waypoints available for animation', {
        position: isMobile ? "top-center" : "top-right",
        className: isMobile ? "toast-mobile" : "",
      });
      return;
    }

    // Stop any existing animation
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    // Ensure static marker is removed when animation starts
    if (animationMarkerRef.current) {
      try {
        if (animationMarkerRef.current.remove) {
          animationMarkerRef.current.remove();
        }
      } catch (error) {
      }
      animationMarkerRef.current = null;
    }

    isAnimatingRef.current = true;
    setIsAnimating(true);
    setShowSpeedControls(false);
    setAnimationSpeed(speed);
    setCurrentAnimationIndex(0);

    // Sort waypoints by sequence to ensure correct order
    const sortedPoints = [...allTripPoints].sort((a, b) => {
      if (a.sequence !== undefined && b.sequence !== undefined) {
        return a.sequence - b.sequence;
      }
      return 0;
    });

    if (sortedPoints.length < 2) {
      toast.error('Need at least 2 waypoints for animation', {
        position: isMobile ? "top-center" : "top-right",
        className: isMobile ? "toast-mobile" : "",
      });
      isAnimatingRef.current = false;
      setIsAnimating(false);
      return;
    }

    // Prepare path data
    const pathData = sortedPoints.map(p => ({
      latitude: parseFloat(p.latitude),
      longitude: parseFloat(p.longitude)
    }));

    // Create truck marker for animation
    const truckIcon = getCarIconRed();
    const startLat = pathData[0].latitude;
    const startLng = pathData[0].longitude;

    try {
      animationMarkerRef.current = new window.mappls.Marker({
        map: mapInstance.current,
        position: { lat: startLat, lng: startLng },
        icon: truckIcon,
        width: 30,
        height: 60,
        fitbounds: false,
        anchor: { x: 15, y: 60 },
        zIndex: 1000
      });
    } catch (error) {
      isAnimatingRef.current = false;
      setIsAnimating(false);
      return;
    }

    // Pre-process points and calculate cumulative distances
    const points = pathData.map(p => ({
      lat: p.latitude,
      lng: p.longitude
    }));

    const distances = [0];
    let totalDistance = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const d = getDistance(points[i].lat, points[i].lng, points[i + 1].lat, points[i + 1].lng);
      totalDistance += d;
      distances.push(totalDistance);
    }

    // Configuration - animation duration based on speed
    const animationDuration = speed === 'fast' ? 15000 : 30000; // 15s for fast, 30s for slow

    // Fit map bounds to show the route path
    try {
      const bounds = new window.mappls.LatLngBounds();
      points.forEach(point => {
        bounds.extend({
          lat: point.lat,
          lng: point.lng
        });
      });
      mapInstance.current.fitBounds(bounds, { 
        padding: 100,
        maxZoom: 16,
        duration: 1000
      });
    } catch (error) {
    }

    let startTime = null;
    let currentRotation = 0;

    // Initial rotation setup
    if (points.length > 1) {
      currentRotation = calculateBearing(points[0].lat, points[0].lng, points[1].lat, points[1].lng) + 180;
    }

    const animate = (timestamp) => {
      if (!isAnimatingRef.current || !animationMarkerRef.current) {
        return;
      }

      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      // Check if animation should loop or stop
      if (elapsed > animationDuration) {
        // Animation complete
        stopAnimation();
        toast.success(`Animation completed! Visited ${points.length} waypoints.`, {
          position: isMobile ? "top-center" : "top-right",
          className: isMobile ? "toast-mobile" : "",
          autoClose: 3000,
        });
        return;
      }

      // Current distance along path
      const targetDist = (elapsed / animationDuration) * totalDistance;

      // Find current segment
      let segmentIndex = 0;
      for (let i = 0; i < distances.length - 1; i++) {
        if (targetDist >= distances[i] && targetDist < distances[i + 1]) {
          segmentIndex = i;
          break;
        }
      }

      // Calculate interpolation factor (t) for this segment
      const segmentStartDist = distances[segmentIndex];
      const segmentEndDist = distances[segmentIndex + 1];
      const segmentLen = segmentEndDist - segmentStartDist;

      // Safety check for zero length segments
      let t = 0;
      if (segmentLen > 0) {
        t = (targetDist - segmentStartDist) / segmentLen;
      }

      // Get segment points
      const p1 = points[segmentIndex];
      const p2 = points[segmentIndex + 1];

      // Interpolate position
      const lat = p1.lat + (p2.lat - p1.lat) * t;
      const lng = p1.lng + (p2.lng - p1.lng) * t;

      // Interpolate rotation
      let targetBearing = currentRotation;
      if (Math.abs(p2.lat - p1.lat) > 0.000001 || Math.abs(p2.lng - p1.lng) > 0.000001) {
        const rawBearing = calculateBearing(p1.lat, p1.lng, p2.lat, p2.lng);
        targetBearing = rawBearing + 180;
      }

      // Smoothly interpolate currentAngle towards targetBearing
      currentRotation = lerpAngle(currentRotation, targetBearing, 0.1);

      // Update Marker position
      if (animationMarkerRef.current) {
        if (animationMarkerRef.current.setPosition) {
          animationMarkerRef.current.setPosition({ lat, lng });
        } else if (animationMarkerRef.current.setLatLng) {
          animationMarkerRef.current.setLatLng([lat, lng]);
        }

        // Update Rotation
        if (hasSetRotation(animationMarkerRef.current)) {
          animationMarkerRef.current.setRotation(currentRotation);
        } else {
          const element = animationMarkerRef.current.getElement ? animationMarkerRef.current.getElement() : null;
          if (element) {
            const img = element.querySelector('img');
            if (img) {
              img.style.transform = `rotate(${currentRotation}deg)`;
            } else {
              element.style.transform = `rotate(${currentRotation}deg)`;
            }
          }
        }
      }

      // Update current animation index for UI
      const currentProgress = elapsed / animationDuration;
      const estimatedIndex = Math.floor(currentProgress * points.length);
      setCurrentAnimationIndex(Math.min(estimatedIndex, points.length - 1));

      // Continue animation
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationFrameIdRef.current = requestAnimationFrame(animate);
  };

  if (error) {
    return (
      <div className="p-4 h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-lg">Map Loading Error</CardTitle>
            <CardDescription>
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p>Possible solutions:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Check your internet connection</li>
                <li>Verify Mappls API credentials</li>
                <li>Try refreshing the page</li>
              </ul>
            </div>
            <Button onClick={handleRefresh} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`flex-1 w-full relative ${isMobile ? 'flex flex-col' : ''}`} style={{ minHeight: isMobile ? '100vh' : 'calc(100vh - 120px)' }}>
      {/* Add CSS for marker animations and infinite scroll/zoom */}
      <style>{`
        #map {
          width: 100% !important;
          height: 100% !important;
          overflow: visible;
          touch-action: pan-x pan-y;
          -webkit-overflow-scrolling: touch;
          position: relative;
        }
        
        /* Fullscreen support */
        .map-container:fullscreen,
        .map-container:-webkit-full-screen,
        .map-container:-moz-full-screen,
        .map-container:-ms-fullscreen {
          width: 100vw !important;
          height: 100vh !important;
          background: #f0f0f0;
        }
        
        .map-container:fullscreen #map,
        .map-container:-webkit-full-screen #map,
        .map-container:-moz-full-screen #map,
        .map-container:-ms-fullscreen #map {
          width: 100% !important;
          height: 100% !important;
          border-radius: 0 !important;
        }
        
        /* Ensure proper sizing when exiting fullscreen - removed transition for faster theme switching */
        #map {
          transition: none;
        }
        
        /* Force proper container sizing */
        .map-container {
          position: relative !important;
          width: 100% !important;
          height: 100% !important;
          overflow: visible !important;
          flex: 1 !important;
          min-width: 0 !important;
          transform: none !important;
          will-change: auto !important;
        }
        
        /* Ensure map container doesn't move when map is panned */
        .map-container > *:not(.map-controls-container):not(.map-points-panel) {
          transform-origin: 0 0;
        }
        
        /* Ensure controls stay visible and positioned correctly - always visible and fixed */
        .map-container {
          position: relative !important;
          transform: none !important;
        }
        
        /* Default positioning for controls (when NOT in fullscreen) - Force position */
        .map-container .map-controls-container {
          position: absolute !important;
          top: 1rem !important;
          right: 1rem !important;
          left: auto !important;
          bottom: auto !important;
          z-index: 20 !important;
          max-width: calc(100% - 2rem) !important;
          box-sizing: border-box !important;
          transform: none !important;
          translate: none !important;
          margin: 0 !important;
          padding: 0 !important;
          pointer-events: auto !important;
          will-change: auto !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 0.5rem !important;
        }
        
        /* Explicitly ensure controls are positioned correctly when NOT in fullscreen */
        .map-container:not(:fullscreen) .map-controls-container,
        .map-container:not(:-webkit-full-screen) .map-controls-container,
        .map-container:not(:-moz-full-screen) .map-controls-container,
        .map-container:not(:-ms-fullscreen) .map-controls-container {
          position: absolute !important;
          top: 1rem !important;
          right: 1rem !important;
          left: auto !important;
          bottom: auto !important;
          z-index: 20 !important;
          max-width: calc(100% - 2rem) !important;
          box-sizing: border-box !important;
          transform: none !important;
          translate: none !important;
          margin: 0 !important;
          padding: 0 !important;
          pointer-events: auto !important;
          will-change: auto !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 0.5rem !important;
        }
        
        /* Override any potential inline styles or browser defaults */
        .map-container .map-controls-container[style] {
          position: absolute !important;
          top: 1rem !important;
          right: 1rem !important;
          left: auto !important;
          bottom: auto !important;
        }
        
        /* Ensure buttons inside controls container are interactive and don't move */
        .map-container .map-controls-container button {
          pointer-events: auto !important;
          position: relative !important;
          transform: none !important;
          translate: none !important;
        }
        
        /* Prevent any movement or translation of controls container */
        .map-container .map-controls-container,
        .map-container:not(:fullscreen) .map-controls-container {
          transform: translate(0, 0) !important;
          translate: 0 0 !important;
          left: auto !important;
          bottom: auto !important;
        }
        
        /* Prevent map transforms from affecting controls */
        .map-container #map {
          position: relative !important;
        }
        
        /* Default positioning for points panel (when NOT in fullscreen) */
        .map-container .map-points-panel {
          position: absolute !important;
          top: 1rem !important;
          left: 1rem !important;
          right: auto !important;
          bottom: auto !important;
          z-index: 20 !important;
          max-width: calc(100% - 2rem) !important;
          box-sizing: border-box !important;
          transform: none !important;
          margin: 0 !important;
          pointer-events: auto !important;
          will-change: auto !important;
        }
        
        /* Explicitly ensure points panel is positioned correctly when NOT in fullscreen */
        .map-container:not(:fullscreen) .map-points-panel,
        .map-container:not(:-webkit-full-screen) .map-points-panel,
        .map-container:not(:-moz-full-screen) .map-points-panel,
        .map-container:not(:-ms-fullscreen) .map-points-panel {
          position: absolute !important;
          top: 1rem !important;
          left: 1rem !important;
          right: auto !important;
          bottom: auto !important;
          z-index: 20 !important;
          max-width: calc(100% - 2rem) !important;
          box-sizing: border-box !important;
          transform: none !important;
          margin: 0 !important;
          pointer-events: auto !important;
          will-change: auto !important;
        }
        
        /* Extra safety: ensure controls are always within viewport bounds */
        .map-container .map-controls-container * {
          max-width: 100% !important;
        }
        
        .map-container .map-points-panel * {
          max-width: 100% !important;
        }
        
        /* Ensure controls stay in bounds even in fullscreen */
        .map-container:fullscreen .map-controls-container,
        .map-container:-webkit-full-screen .map-controls-container,
        .map-container:-moz-full-screen .map-controls-container,
        .map-container:-ms-fullscreen .map-controls-container {
          top: 1rem !important;
          right: 1rem !important;
          left: auto !important;
          max-width: calc(100vw - 2rem) !important;
        }
        
        .map-container:fullscreen .map-points-panel,
        .map-container:-webkit-full-screen .map-points-panel,
        .map-container:-moz-full-screen .map-points-panel,
        .map-container:-ms-fullscreen .map-points-panel {
          top: 1rem !important;
          left: 1rem !important;
          right: auto !important;
          max-width: calc(100vw - 2rem) !important;
        }
        
        @keyframes pulse {
          0% { 
            transform: scale(1); 
            opacity: 1; 
          }
          50% { 
            transform: scale(1.2); 
            opacity: 0.8; 
          }
          100% { 
            transform: scale(1); 
            opacity: 1; 
          }
        }
        
        @keyframes glow {
          0% { 
            box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 5px rgba(59, 130, 246, 0.5);
          }
          50% { 
            box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.6);
          }
          100% { 
            box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 5px rgba(59, 130, 246, 0.5);
          }
        }
        
        .start-marker {
          animation: pulse 2s infinite, glow 3s infinite;
        }
        
        .end-marker {
          animation: pulse 2s infinite;
        }
        
        .end-marker::after {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(45deg, #ef4444, #dc2626, #ef4444);
          border-radius: 50%;
          z-index: -1;
          animation: pulse 2s infinite;
        }
        
        .highlighted-point {
          animation: pulse 1s infinite !important;
          z-index: 1000 !important;
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
        }
        
        .selected-point {
          z-index: 1001 !important;
        }
        
        @keyframes highlight-pulse {
          0% { 
            transform: scale(1); 
            box-shadow: 0 0 0 rgba(59, 130, 246, 0.8);
          }
          50% { 
            transform: scale(1.3); 
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
          }
          100% { 
            transform: scale(1); 
            box-shadow: 0 0 0 rgba(59, 130, 246, 0.8);
          }
        }
        
        /* Ensure map container supports infinite scroll */
        .map-container {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: visible;
        }
        
        
        
        /* Specific point marker should be visible above blue overlay */
        .specific-point-marker {
          z-index: 20 !important;
          position: relative;
        }
        
        @keyframes bluePulse {
          0% { 
            background: rgba(59, 130, 246, 0.3);
          }
          50% { 
            background: rgba(59, 130, 246, 0.5);
          }
          100% { 
            background: rgba(59, 130, 246, 0.3);
          }
        }
        
        /* Force marker colors to override any external styles */
        .mappls-marker div {
          background: inherit !important;
        }
        
        .mappls-marker div[style*="background"] {
          background: inherit !important;
        }
        
        /* Mobile-specific styles */
        @media (max-width: 768px) {
          .mobile-map-container {
            height: 100vh !important;
            min-height: 100vh !important;
            transition: height 0.3s ease;
          }
          
          .mobile-map-container.details-open {
            height: 40vh !important;
            min-height: 40vh !important;
          }
          
          .mobile-card-container {
            height: 40vh !important;
            min-height: 40vh !important;
            max-height: 40vh !important;
            overflow-y: auto;
            overflow-x: hidden;
            transition: transform 0.3s ease, height 0.3s ease;
            transform: translateY(0);
            will-change: transform, height;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 20;
            -webkit-overflow-scrolling: touch;
            border-radius: 16px 16px 0 0;
          }
          
          .mobile-card-container.expanded {
            height: 75vh !important;
            min-height: 75vh !important;
            max-height: 75vh !important;
            transform: translateY(0);
            z-index: 20;
            box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.3);
          }
          
          .mobile-card-container.collapsed {
            height: 20vh !important;
            min-height: 20vh !important;
            max-height: 20vh !important;
            transform: translateY(0);
            box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.2);
          }
          
          .mobile-card-container.normal {
            height: 40vh !important;
            min-height: 40vh !important;
            max-height: 40vh !important;
            transform: translateY(0) !important;
          }

          .mobile-card-container.dragging {
            transition: none !important;
            z-index: 20;
          }

          .mobile-card-drag-handle {
            height: 4px;
            background: #e5e7eb;
            border-radius: 2px;
            margin: 8px auto;
            width: 40px;
            cursor: grab;
            transition: none;
          }

          .mobile-card-drag-handle:hover {
            background: #9ca3af;
          }

          .mobile-card-drag-handle:active {
            cursor: grabbing;
          }

          .map-blur-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(8px);
            z-index: 5;
            opacity: 0;
            transition: none;
            pointer-events: none;
          }

          .map-blur-overlay.active {
            opacity: 1;
            pointer-events: auto;
          }
          
          .mobile-black-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.85);
            z-index: 5;
            opacity: 0;
            transition: opacity 0.3s ease;
            pointer-events: none;
          }

          .mobile-black-overlay.active {
            opacity: 1;
            pointer-events: auto;
          }
          
          .mobile-controls {
            position: absolute !important;
            top: 10px !important;
            right: 10px !important;
            z-index: 30 !important;
          }
          
          .mobile-controls button {
            padding: 6px !important;
            margin-bottom: 4px !important;
            font-size: 12px !important;
            min-width: 36px !important;
            min-height: 36px !important;
            width: 36px !important;
            height: 36px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .mobile-controls button svg {
            margin: 0 !important;
          }
          
          .mobile-points-panel {
            position: absolute !important;
            top: 10px !important;
            left: 10px !important;
            right: 10px !important;
            z-index: 30 !important;
            max-height: 50vh !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          /* Smooth scrolling for mobile card content */
          .mobile-card-container::-webkit-scrollbar {
            width: 6px;
          }
          
          .mobile-card-container::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 3px;
          }
          
          .mobile-card-container::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 3px;
          }
          
          .mobile-card-container::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
          
          /* Ensure content inside mobile card can scroll */
          .mobile-card-container .p-4,
          .mobile-card-container .p-6 {
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          /* Scrollbar styles for mobile points panel */
          .mobile-points-panel::-webkit-scrollbar {
            width: 4px;
          }
          
          .mobile-points-panel::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.1);
            border-radius: 2px;
          }
          
          .mobile-points-panel::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 2px;
          }
          
          .mobile-points-panel::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.5);
          }
        }
        
        /* Attractive placeholder animations */
        @keyframes float {
          0%, 100% { 
            transform: translateY(0px); 
          }
          50% { 
            transform: translateY(-10px); 
          }
        }
        
        @keyframes shimmer {
          0% { 
            background-position: -200% 0; 
          }
          100% { 
            background-position: 200% 0; 
          }
        }
        
        @keyframes glow-pulse {
          0%, 100% { 
            box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
          }
          50% { 
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.4);
          }
        }
        
        @keyframes slideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutDown {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes bounceIn {
          0% {
            transform: scale(0.3);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .floating-icon {
          animation: float 3s ease-in-out infinite;
        }
        
        .shimmer-text {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .glow-card {
          animation: glow-pulse 2s ease-in-out infinite;
        }
        
        .mobile-card-container {
          animation: slideInUp 0.3s ease-out;
        }
        
        .mobile-card-container .p-4,
        .mobile-card-container .p-6 {
          animation: bounceIn 0.6s ease-out 0.2s both;
        }
        
        /* Smooth content transitions - removed for faster theme switching */
        .mobile-card-container .space-y-3 > * {
          transition: none;
        }
        
        .mobile-card-container .grid > * {
          transition: none;
        }
        
        /* Enhanced touch interactions */
        .mobile-card-container:active {
          transform: scale(0.98);
        }
        
        .mobile-card-container.expanded:active {
          transform: translateY(-10vh) scale(0.98);
        }
        
        .mobile-card-container.collapsed:active {
          transform: translateY(20vh) scale(0.98);
        }
        
        /* Smooth scrolling behavior */
        .mobile-card-container {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Enhanced button animations - removed transition for faster theme switching */
        .mobile-card-container button {
          transition: none;
        }
        
        .mobile-card-container button:active {
          transform: scale(0.95);
        }
        
        /* Content fade animations */
        .mobile-card-container .space-y-3 > * {
          animation: fadeInUp 0.4s ease-out;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Staggered animation for grid items */
        .mobile-card-container .grid > *:nth-child(1) { animation-delay: 0.1s; }
        .mobile-card-container .grid > *:nth-child(2) { animation-delay: 0.2s; }
        .mobile-card-container .grid > *:nth-child(3) { animation-delay: 0.3s; }
        .mobile-card-container .grid > *:nth-child(4) { animation-delay: 0.4s; }
        
        /* Mobile toast styling - smaller and faster */
        .toast-mobile {
          font-size: 12px !important;
          padding: 8px 12px !important;
          min-width: 200px !important;
          max-width: 300px !important;
        }
        
        .toast-mobile-body {
          font-size: 12px !important;
          padding: 4px !important;
        }
        
        .toast-mobile-progress {
          height: 2px !important;
        }
        
        /* Skeleton Loading Animations */
        @keyframes skeleton-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .skeleton-pulse {
          animation: skeleton-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .skeleton-shimmer {
          background: linear-gradient(90deg, 
            rgba(255, 255, 255, 0) 0%, 
            rgba(255, 255, 255, 0.2) 20%, 
            rgba(255, 255, 255, 0.5) 60%, 
            rgba(255, 255, 255, 0) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
        
        .dark .skeleton-shimmer {
          background: linear-gradient(90deg, 
            rgba(255, 255, 255, 0) 0%, 
            rgba(255, 255, 255, 0.05) 20%, 
            rgba(255, 255, 255, 0.1) 60%, 
            rgba(255, 255, 255, 0) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        /* Car bounce animation for smooth movement */
        @keyframes carBounce {
          0%, 100% {
            transform: translateY(0) scale(1);
            filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.6));
          }
          50% {
            transform: translateY(-3px) scale(1.05);
            filter: drop-shadow(0 0 12px rgba(239, 68, 68, 0.8));
          }
        }
        
        /* Car movement animation - smooth transition */
        @keyframes carMove {
          0% {
            transform: translateX(0) translateY(0);
          }
          100% {
            transform: translateX(0) translateY(0);
          }
        }
        
        /* Ensure animation car marker is visible above all other markers */
        .animation-car-marker,
        .mappls-marker[style*="z-index: 1000"] {
          z-index: 1000 !important;
          animation: carBounce 2s ease-in-out infinite !important;
          pointer-events: none !important;
          transition: transform 0.3s ease-out !important;
        }
        
        /* Make sure the animation marker element itself has the animation */
        .animation-car-marker img,
        .animation-car-marker svg {
          animation: carBounce 2s ease-in-out infinite;
          filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.6));
        }
        
      `}</style>
      {/* Loading Skeleton */}
      {isLoading && (
        <div className="absolute inset-0 bg-background z-10">
          {/* Desktop Skeleton */}
          {!isMobile && (
            <div className="h-full w-full relative">
              {/* Map Skeleton */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-xl">
                {/* Map Controls Skeleton */}
                <div className="absolute top-4 right-4 space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-8 w-20 bg-white/60 dark:bg-gray-700/60 rounded-md skeleton-pulse skeleton-shimmer"></div>
                  ))}
                </div>
                
                {/* Points Panel Skeleton */}
                <div className="absolute top-4 left-4 w-80 h-96 bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-lg skeleton-pulse">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="h-6 w-32 bg-gray-300 dark:bg-gray-600 rounded mb-2 skeleton-shimmer"></div>
                    <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                  </div>
                  <div className="p-4 space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full skeleton-shimmer"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded skeleton-shimmer"></div>
                          <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Map Content Skeleton */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto skeleton-pulse skeleton-shimmer"></div>
                    <div className="space-y-2">
                      <div className="h-6 w-48 bg-gray-300 dark:bg-gray-600 rounded mx-auto skeleton-shimmer"></div>
                      <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded mx-auto skeleton-shimmer"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Mobile Skeleton */}
          {isMobile && (
            <div className="h-screen w-full flex flex-col">
              {/* Map Area Skeleton */}
              <div className="flex-1 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 relative">
                {/* Mobile Controls Skeleton */}
                <div className="absolute top-2 right-2 space-y-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-6 w-16 bg-white/60 dark:bg-gray-700/60 rounded skeleton-pulse skeleton-shimmer"></div>
                  ))}
                </div>
                
                {/* Mobile Points Panel Skeleton */}
                <div className="absolute top-2 left-2 right-2 h-48 bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-lg skeleton-pulse">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="h-5 w-24 bg-gray-300 dark:bg-gray-600 rounded mb-1 skeleton-shimmer"></div>
                    <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                  </div>
                  <div className="p-2 space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full skeleton-shimmer"></div>
                        <div className="flex-1 space-y-1">
                          <div className="h-3 w-20 bg-gray-300 dark:bg-gray-600 rounded skeleton-shimmer"></div>
                          <div className="h-2 w-28 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Map Content Skeleton */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto skeleton-pulse skeleton-shimmer"></div>
                    <div className="space-y-1">
                      <div className="h-4 w-32 bg-gray-300 dark:bg-gray-600 rounded mx-auto skeleton-shimmer"></div>
                      <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded mx-auto skeleton-shimmer"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Mobile Card Skeleton */}
              <div className="h-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-t-xl shadow-lg skeleton-pulse">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-5 w-32 bg-gray-300 dark:bg-gray-600 rounded skeleton-shimmer"></div>
                    <div className="flex space-x-1">
                      <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded skeleton-shimmer"></div>
                      <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded skeleton-shimmer"></div>
                      <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded skeleton-shimmer"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                      <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                    </div>
                    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded skeleton-shimmer"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div className={`${isMobile ? `mobile-map-container ${showMobileDetails || selectedPointDetails ? 'details-open' : ''}` : 'h-full'} w-full relative map-container ${isFullscreen ? 'fullscreen' : ''} bg-muted-foreground/10`}>
        <div 
          ref={mapRef}
          id="map" 
          className={`${isMobile ? 'mobile-map-container' : 'h-full'} w-full ${isMobile ? 'rounded-t-xl' : 'rounded-xl'} shadow-lg`}
          style={{
            width: '100%',
            height: isMobile ? (showMobileDetails || selectedPointDetails ? '40vh' : '100vh') : '100%',
            minHeight: isMobile ? (showMobileDetails || selectedPointDetails ? '40vh' : '100vh') : '400px',
            position: 'relative'
          }}
        />
        {/* Blue overlay for highlighting */}
        {mapHighlighted && (
          <div className={`map-blue-overlay ${isMobile ? 'rounded-t-xl' : 'rounded-xl'}`}></div>
        )}
        {/* Blur overlay for mobile when card is expanded */}
        {isMobile && cardState === 'expanded' && (
          <div 
            key={`blur-${cardState}`}
            className={`map-blur-overlay ${isMobile ? 'rounded-t-xl' : 'rounded-xl'} active`}
          ></div>
        )}
        {/* Black overlay for mobile when details are open */}
        {isMobile && showMobileDetails && (
          <div 
            className={`mobile-black-overlay ${isMobile ? 'rounded-t-xl' : 'rounded-xl'} active`}
          ></div>
        )}
        
        {/* Map Controls - Moved inside map container */}
        {mapLoaded && (
          <div 
            className={`${isMobile ? 'mobile-controls' : 'absolute top-4 right-4'} z-20 space-y-2 map-controls-container`}
            ref={(el) => {
              if (el && !isMobile) {
                
              }
            }}
          >
          <Button
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            onClick={handleRefresh}
            className="bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-gray-100 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            <RefreshCw className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
            {!isMobile && 'Refresh'}
          </Button>
          
         
          
          <Button
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            onClick={() => setShowFilter(!showFilter)}
            className="bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-gray-100 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            <MapPin className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
            {!isMobile && (showFilter ? 'Hide' : 'Show')} {!isMobile && 'WayPoints'}
          </Button>
          
          {/* Animation Button */}
          <Button
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            onClick={toggleAnimationControls}
            className="bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-gray-100 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            {isAnimating ? (
              <>
                <Pause className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
                {!isMobile && 'Stop'}
              </>
            ) : (
              <>
                <Play className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
                {!isMobile && 'Animation'}
              </>
            )}
          </Button>

          {/* Speed Controls */}
          {showSpeedControls && !isAnimating && (
            <div className={`${isMobile ? 'flex flex-col gap-2' : 'flex gap-2'}`}>
              <Button
                variant="outline"
                size={isMobile ? "sm" : "sm"}
                onClick={() => startAnimation('fast')}
                className="bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-gray-100 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              >
                <Zap className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
                {!isMobile && 'Speed'}
              </Button>
              <Button
                variant="outline"
                size={isMobile ? "sm" : "sm"}
                onClick={() => startAnimation('slow')}
                className="bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-gray-100 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
              >
                <Clock className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
                {!isMobile && 'Slow'}
              </Button>
            </div>
          )}
          
          <Button
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            onClick={isFullscreen ? handleRefresh : toggleFullscreen}
            className="bg-white text-gray-900 border border-gray-200 shadow-md hover:bg-gray-100 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            {isFullscreen ? (
              <>
                <RefreshCw className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
                {!isMobile && 'Exit Fullscreen'}
              </>
            ) : (
              <>
                <Maximize className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${isMobile ? '' : 'mr-2'} text-gray-700`} />
                {!isMobile && 'Fullscreen'}
              </>
            )}
          </Button>
          </div>
        )}
        
        {/* Points Filter Panel - Moved inside map container */}
        {showFilter && mapLoaded && (
          <div className={`${isMobile ? 'mobile-points-panel' : 'absolute top-4 left-4 z-20 w-80 max-h-96'} overflow-y-auto bg-background/95 backdrop-blur-sm rounded-lg shadow-lg border border-border map-points-panel`}>
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Trip WayPoints</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilter(false)}
                className="h-6 w-6 p-0 text-gray-700"
              >
                ×
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {allTripPoints.length} WayPoints found • {visiblePoints.size} visible on map
            </p>
          </div>
          
          <div className="p-2 max-h-80 overflow-y-auto">
            {allTripPoints.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No trip WayPoints available</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allTripPoints.map((point, index) => {
                  const pointKey = `${point.tripId}-${point.id}`;
                  const isVisible = visiblePoints.has(pointKey);
                  const isHighlighted = highlightedPoint === point.id;
                  const isProcessing = processingPoint === point.id;
                  
                  return (
                    <div
                      key={pointKey}
                      className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                        isProcessing
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 shadow-md'
                          : isHighlighted 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 shadow-md' 
                          : isVisible
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                          : 'bg-card border-border hover:border-muted-foreground/50'
                      }`}
                      onClick={() => !isProcessing && highlightPoint(point.id, point.tripId)}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Point Icon */}
                        <div className="relative">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-background shadow-sm flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: point.color }}
                          />
                          {isVisible && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border border-background"></div>
                          )}
                        </div>
                        
                        {/* Point Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-foreground truncate">
                              {point.isStart ? '🚀 Start' : point.isEnd ? '🏁 End' : `📍 WayPoint ${point.sequence}`}
                            </h4>
                            <span className="text-xs text-muted-foreground">
                              {point.tripName}
                            </span>
                            {isProcessing && (
                              <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded-full flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Processing...
                              </span>
                            )}
                            {isVisible && !isProcessing && (
                              <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                                On Map
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-1 space-y-1">
                            <p className="text-xs text-muted-foreground border-1 border-muted-foreground/20">
                              <strong>Speed:</strong> {point.speed} km/h
                            </p>
                            <p className="text-xs text-muted-foreground">
                              <strong>Time:</strong> {point.pointTime ? new Date(point.pointTime).toLocaleString() : 
                                                      point.point_time ? new Date(point.point_time).toLocaleString() : 'N/A'}
                            </p>
                           
                            <p className="text-xs text-muted-foreground">
                              <strong>Terminal ID:</strong> {point.terminal_id || point.terminalId}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {point.sequence} of {point.totalPoints} • {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {highlightedPoint && (
            <div className="p-3 border-t border-border bg-muted/50">
              <Button
                variant="outline"
                size="sm"
                onClick={clearHighlight}
                className="w-full"
              >
                Clear Highlight
              </Button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Mobile Card Container - Show when details are open or when a point is selected */}
      {isMobile && (showMobileDetails || selectedPointDetails) && (
        <div 
          className={`mobile-card-container ${showMobileDetails || selectedPointDetails ? 'bg-black' : 'bg-muted-foreground/10'} border-t border-gray-200 text-foreground shadow-lg ${cardState} ${isDragging ? 'dragging' : ''}`}
          style={{
            height: `${cardHeight}vh`,
            minHeight: `${cardHeight}vh`,
            maxHeight: `${cardHeight}vh`,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 20,
          }}
        >
          {/* Drag Handle */}
          <div 
            className="mobile-card-drag-handle"
            onTouchStart={(e) => {
              e.stopPropagation();
              handleDragStart(e);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleDragStart(e);
            }}
          ></div>
          {selectedPointDetails ? (
            // Point Details Card
            <div className="p-4">
              
              
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {selectedPointDetails.isStart ? '🚀 Trip Start' : selectedPointDetails.isEnd ? '🏁 Trip End' : `📍 WayPoint ${selectedPointDetails.sequence}`}
                </h3>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={expandMobileCard}
                    className="h-8 w-8 p-0 text-xs"
                    title={cardState === 'expanded' ? 'Normal Size' : 'Expand'}
                  >
                    {cardState === 'expanded' ? '↕' : '⤢'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={collapseMobileCard}
                    className="h-8 w-8 p-0 text-xs"
                    title={cardState === 'collapsed' ? 'Normal Size' : 'Collapse'}
                  >
                    {cardState === 'collapsed' ? '↕' : '⤡'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closeMobileCard}
                    className="h-8 w-8 p-0"
                    title="Close"
                  >
                    ×
                  </Button>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Trip Information */}
                
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">Trip Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-blue-600 font-medium">Trip ID:</span>
                      <p className="text-gray-900">{selectedPointDetails.tripId || selectedPointDetails.terminalId}</p>
                    </div>
                    <div>
                      <span className="text-blue-600 font-medium">Status:</span>
                      <p className="text-gray-900 capitalize">{selectedPointDetails.tripStatus || 'Active'}</p>
                    </div>
                    <div>
                      <span className="text-blue-600 font-medium">Start Time:</span>
                      <p className="text-gray-900">
                        {formatDateTime(selectedPointDetails.tripStartTime)}
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-600 font-medium">End Time:</span>
                      <p className="text-gray-900">
                        {formatDateTime(selectedPointDetails.tripEndTime)}
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-600 font-medium">Duration:</span>
                      <p className="text-gray-900">{selectedPointDetails.duration || 'N/A'} min</p>
                    </div>
                    <div>
                      <span className="text-blue-600 font-medium">Distance:</span>
                      <p className="text-gray-900">{selectedPointDetails.distance ? selectedPointDetails.distance.toFixed(2) : 'N/A'} km</p>
                    </div>
                    {selectedPointDetails.created_at && (
                      <div className="col-span-2">
                        <span className="text-blue-600 font-medium">Created At:</span>
                        <p className="text-gray-900">
                          {formatDate(selectedPointDetails.created_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Trip Coordinates */}
                {(selectedPointDetails.start_lat || selectedPointDetails.end_lat) && (
                  <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                    <h4 className="text-sm font-semibold text-purple-800 mb-2">Trip Coordinates</h4>
                    <div className="space-y-2 text-xs">
                      {selectedPointDetails.start_lat && (
                        <div>
                          <span className="text-purple-600 font-medium">Start: </span>
                          <span className="text-gray-900">{selectedPointDetails.start_lat.toFixed(6)}, {selectedPointDetails.start_lng.toFixed(6)}</span>
                        </div>
                      )}
                      {selectedPointDetails.end_lat && (
                        <div>
                          <span className="text-purple-600 font-medium">End: </span>
                          <span className="text-gray-900">{selectedPointDetails.end_lat.toFixed(6)}, {selectedPointDetails.end_lng.toFixed(6)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Point Details */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">WayPoint ID</p>
                    <p className="text-sm font-medium text-gray-900">{selectedPointDetails.id}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Speed</p>
                    <p className="text-sm font-medium text-gray-900">{selectedPointDetails.speed} km/h</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Sequence</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedPointDetails.sequence} of {selectedPointDetails.totalPoints}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Terminal ID</p>
                    <p className="text-sm font-medium text-gray-900">{selectedPointDetails.terminalId || selectedPointDetails.terminal_id}</p>
                  </div>
                </div>
                
                {/* Point Time Information */}
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">WayPoint Time Information</h4>
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-blue-600 font-medium">WayPoint Time:</span>
                      <p className="text-gray-900">
                        {selectedPointDetails.pointTime ? new Date(selectedPointDetails.pointTime).toLocaleString() : 
                         selectedPointDetails.point_time ? new Date(selectedPointDetails.point_time).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600 font-medium">Raw Time:</span>
                      <p className="text-gray-900 font-mono text-xs">
                        {selectedPointDetails.point_time || selectedPointDetails.pointTime || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Coordinates */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Coordinates</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Latitude:</span>
                      <p className="font-medium text-gray-900">{selectedPointDetails.latitude.toFixed(6)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Longitude:</span>
                      <p className="font-medium text-gray-900">{selectedPointDetails.longitude.toFixed(6)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Point Type Indicators */}
                {selectedPointDetails.isStart && (
                  <div 
                    className="bg-green-50 border border-green-200 p-3 rounded-lg cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => {
                      // Find the start point in allTripPoints and highlight it
                      const startPoint = allTripPoints.find(p => 
                        p.id === selectedPointDetails.id && 
                        p.tripId === selectedPointDetails.tripId
                      );
                      if (startPoint) {
                        highlightPoint(startPoint.id, startPoint.tripId);
                        // Close the current card to show the highlighted point
                        closeMobileCardOnly();
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">🚀</span>
                        <div>
                          <p className="text-sm font-medium text-green-800">Trip Start WayPoint</p>
                          <p className="text-xs text-green-600">Click to view on map</p>
                        </div>
                      </div>
                      <div className="text-green-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedPointDetails.isEnd && (
                  <div 
                    className="bg-red-50 border border-red-200 p-3 rounded-lg cursor-pointer hover:bg-red-100 transition-colors duration-200"
                    onClick={() => {
                      // Find the end point in allTripPoints and highlight it
                      const endPoint = allTripPoints.find(p => 
                        p.id === selectedPointDetails.id && 
                        p.tripId === selectedPointDetails.tripId
                      );
                      if (endPoint) {
                        highlightPoint(endPoint.id, endPoint.tripId);
                        // Close the current card to show the highlighted point
                        closeMobileCardOnly();
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">🏁</span>
                        <div>
                          <p className="text-sm font-medium text-red-800">Trip End WayPoint</p>
                          <p className="text-xs text-red-600">Click to view on map</p>
                        </div>
                      </div>
                      <div className="text-red-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                
                {!selectedPointDetails.isStart && !selectedPointDetails.isEnd && (
                  <div 
                    className="bg-blue-50 border border-blue-200 p-3 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors duration-200"
                    onClick={() => {
                      // Find the waypoint in allTripPoints and highlight it
                      const waypoint = allTripPoints.find(p => 
                        p.id === selectedPointDetails.id && 
                        p.tripId === selectedPointDetails.tripId
                      );
                      if (waypoint) {
                        highlightPoint(waypoint.id, waypoint.tripId);
                        // Close the current card to show the highlighted point
                        closeMobileCardOnly();
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">📍</span>
                        <div>
                          <p className="text-sm font-medium text-blue-800">Waypoint</p>
                          <p className="text-xs text-blue-600">Click to view on map</p>
                        </div>
                      </div>
                      <div className="text-blue-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Back to Points Button */}
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={closeMobileCardOnly}
                    className="w-full"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Back to WayPoints
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Placeholder Card with attractive content
            <div className={`p-6 text-center ${showMobileDetails ? 'text-white' : ''}`}>
              {/* Close Button for Mobile Details */}
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${showMobileDetails ? 'text-white' : 'text-foreground'}`}>Trip Details</h3>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={expandMobileCard}
                    className="h-8 w-8 p-0 text-xs opacity-60 hover:opacity-100"
                    title={cardState === 'expanded' ? 'Normal Size' : 'Expand'}
                  >
                    {cardState === 'expanded' ? '↕' : '⤢'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={collapseMobileCard}
                    className="h-8 w-8 p-0 text-xs opacity-60 hover:opacity-100"
                    title={cardState === 'collapsed' ? 'Normal Size' : 'Collapse'}
                  >
                    {cardState === 'collapsed' ? '↕' : '⤡'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closeMobileCard}
                    className="h-8 w-8 p-0"
                    title="Close Details"
                  >
                    ×
                  </Button>
                </div>
              </div>
              
              <div className="mb-6">
                {/* Animated Map Icon */}
                <div className="relative mx-auto w-20 h-20 mb-4 floating-icon">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse glow-card"></div>
                  <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center">
                    <MapPin className="h-8 w-8 text-blue-600 animate-bounce" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
                
                {/* Main Title */}
                <h3 className="text-xl font-bold text-gray-800 mb-2 shimmer-text">
                  🗺️ Track Your Wheels
                </h3>
                
                {/* Subtitle */}
                <p className="text-sm text-foreground mb-4">
                  Interactive Trip Tracking Dashboard
                </p>
              </div>
              
              {/* Feature Cards */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg border border-white/20/80 transition-all duration-300 glow-card">
                  <div className="text-2xl mb-1 floating-icon">🚀</div>
                  <p className="text-xs font-medium text-gray-700">Trip Start</p>
                  <p className="text-xs text-foreground">Green markers</p>
                </div>
                <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg border border-white/20/80 transition-all duration-300 glow-card">
                  <div className="text-2xl mb-1 floating-icon">🏁</div>
                  <p className="text-xs font-medium text-gray-700">Trip End</p>
                  <p className="text-xs text-foreground">Red markers</p>
                </div>
                <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg border border-white/20/80 transition-all duration-300 glow-card">
                  <div className="text-2xl mb-1 floating-icon">📍</div>
                  <p className="text-xs font-medium text-gray-700">Waypoints</p>
                  <p className="text-xs text-foreground">Blue markers</p>
                </div>
                <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg border border-white/20/80 transition-all duration-300 glow-card">
                  <div className="text-2xl mb-1 floating-icon">📊</div>
                  <p className="text-xs font-medium text-gray-700">Analytics</p>
                  <p className="text-xs text-foreground">Real-time data</p>
                </div>
              </div>
              
              {/* Call to Action */}
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-2 text-sm text-foreground">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="shimmer-text">Tap "WayPoints" to explore trip details</span>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200 glow-card">
                  <p className="text-sm font-medium text-gray-800 mb-1">
                    🎯 Ready to Explore?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Select any point from the list above to view detailed information, speed, coordinates, and more!
                  </p>
                </div>
              </div>
              
              {/* Stats */}
              {allTripPoints.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-center space-x-6 text-xs text-muted-foreground">
                      <div className="text-center">
                      <div className="font-semibold text-muted-foreground animate-pulse">{allTripPoints.length}</div>
                      <div>Total WayPoints</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-muted-foreground animate-pulse">{vehicles.length}</div>
                      <div>Active Trips</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-muted-foreground animate-pulse">{visiblePoints.size}</div>
                      <div>On Map</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      
    </div>
  );
}